import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, Star } from "lucide-react";

interface PlanSelectionStepProps {
  selectedPlan: 'monthly' | 'annual' | null;
  onSelectPlan: (plan: 'monthly' | 'annual') => void;
  onNext: () => void;
}

const PlanSelectionStep = ({ selectedPlan, onSelectPlan, onNext }: PlanSelectionStepProps) => {
  const plans = [
    {
      id: 'monthly' as const,
      name: 'Monthly Shark',
      price: '$34.99',
      oldPrice: '$68',
      period: '/month',
      description: 'Perfect for getting started',
      features: [
        'Unlimited property uploads',
        'Access to buyers in all markets',
        'View buyer activity',
        'Priority email & phone support'
      ]
    },
    {
      id: 'annual' as const,
      name: 'Annual Shark',
      price: '$349.99',
      oldPrice: '$680',
      period: '/year',
      description: 'Best value - Save $69.89 vs. monthly',
      popular: true,
      savings: 'Save 49% vs. previous pricing',
      features: [
        'Unlimited property uploads',
        'Access to buyers in all markets',
        'View buyer activity',
        'Priority email & phone support',
        'Early access to new features'
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h2 className="text-2xl font-bold">Choose Your Plan</h2>
        <p className="text-muted-foreground">Start with a 30-day free trial, then continue with your selected plan</p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {plans.map((plan) => (
          <Card 
            key={plan.id}
            className={`relative min-w-0 cursor-pointer transition-all ${
              selectedPlan === plan.id 
                ? 'ring-2 ring-primary border-primary' 
                : 'hover:border-primary/50'
            }`}
            onClick={() => onSelectPlan(plan.id)}
          >
            {plan.popular && (
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <Badge className="bg-primary text-primary-foreground whitespace-nowrap">
                  <Star className="w-3 h-3 mr-1" />
                  Most Popular
                </Badge>
              </div>
            )}
            
            <CardHeader className="text-center">
              <CardTitle className="text-xl">{plan.name}</CardTitle>
              <div className="space-y-1 min-w-0">
                {plan.oldPrice && (
                  <span className="block text-base font-medium text-muted-foreground line-through">{plan.oldPrice}</span>
                )}
                <div className="flex flex-wrap justify-center items-baseline gap-x-1 text-2xl lg:text-3xl font-bold">
                  <span>{plan.price}</span>
                  <span className="text-base lg:text-lg font-normal text-muted-foreground">{plan.period}</span>
                </div>
                {plan.savings && (
                  <div className="text-sm text-green-600 font-medium">{plan.savings}</div>
                )}
              </div>
              <CardDescription>{plan.description}</CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-center space-x-2">
                    <Check className="w-4 h-4 text-green-500 shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
              
              {selectedPlan === plan.id && (
                <div className="pt-2">
                  <Badge variant="outline" className="w-full justify-center">
                    Selected Plan
                  </Badge>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="text-center space-y-4">
        <div className="p-4 bg-muted rounded-lg">
          <p className="text-sm text-muted-foreground">
            <strong>30-Day Free Trial</strong> - No charges until your trial ends. 
            Cancel anytime during the trial period.
          </p>
        </div>

        <Button 
          onClick={onNext}
          disabled={!selectedPlan}
          className="w-full max-w-md"
        >
          Continue to Payment Setup
        </Button>
      </div>
    </div>
  );
};

export default PlanSelectionStep;